/**
 * Variant formats of a content item (spec 40 §4.4, REQ-40-007) and their
 * platform mapping, used by modules 40, 50, 60 and 70.
 */
export const CONTENT_VARIANT_FORMATS = ['ig_feed', 'ig_carousel', 'ig_story', 'fb_feed', 'ig_reel', 'fb_reel', 'fb_story'];
/** Formats accepted on create; the reel formats joined with S70-9 (reel publishing, module 100), `fb_story` with S560-1. */
export const ENABLED_VARIANT_FORMATS = ['ig_feed', 'ig_carousel', 'ig_story', 'ig_reel', 'fb_feed', 'fb_reel', 'fb_story'];
/**
 * Formats AI preparation (module 80) writes: image posts, and the Instagram reel, whose storyboard module 100's
 * storyboarder writes (E400). The Facebook reel follows its Instagram source once S260-5 mirrors storyboards.
 */
export const AI_PREPARABLE_FORMATS = ['ig_feed', 'ig_carousel', 'ig_story', 'fb_feed', 'ig_reel'];
/** `ig_* -> instagram`, `fb_* -> facebook`. */
export function platformOfFormat(format) {
    return format.startsWith('ig_') ? 'instagram' : 'facebook';
}
/**
 * Most photos of one Facebook multi-photo post (spec 40 §4.4d). PROVISIONAL: Meta's
 * documentation names no maximum (a third-party blog says 4); verified in T260-7-1
 * (spec 40 §9 Q24). Equal to the Instagram carousel until then.
 */
export const FB_MULTI_PHOTO_MAX = 10;
/**
 * Most photos of one Instagram story (S480-2, ADR-035): each photo is its own story frame,
 * published one after the other. Meta names no maximum for API stories; the value is our
 * product limit, equal to the carousel.
 */
export const IG_STORY_MAX_FRAMES = 10;
/**
 * The story formats (S560-1, ADR-041): one 9:16 frame per photo, or one video story from module 100's render.
 * A Facebook page story follows the same rules as the Instagram story.
 */
export const STORY_FORMATS = ['ig_story', 'fb_story'];
export function isStoryFormat(format) {
    return STORY_FORMATS.includes(format);
}
/** Formats Meta publishes without a caption (`media_type=STORIES` and `photo_stories`/`video_stories` take none), so an empty text is fine. */
export const CAPTIONLESS_FORMATS = ['ig_story', 'fb_story'];
export function isCaptionlessFormat(format) {
    return CAPTIONLESS_FORMATS.includes(format);
}
/**
 * Link sources of a Facebook format (spec 40 §4.4d, ADR-029), in preference order:
 * the Instagram variant a linked Facebook variant follows. One rule for web and API.
 */
export const LINK_SOURCES = {
    fb_feed: ['ig_feed', 'ig_carousel'],
    fb_reel: ['ig_reel'],
    fb_story: ['ig_story'],
};
/** The formats that can follow an Instagram variant. */
export const LINK_TARGET_FORMATS = Object.keys(LINK_SOURCES);
export function isLinkTargetFormat(format) {
    return Object.prototype.hasOwnProperty.call(LINK_SOURCES, format);
}
/** Source formats of `target`, preferred first (`[]` for a format that cannot be linked). */
export function linkSourceFormats(format) {
    return isLinkTargetFormat(format) ? LINK_SOURCES[format] : [];
}
/**
 * Most media one variant of a format may carry (spec 40 §5.1 `too_many_media`,
 * §9 Q9): a carousel holds at most 10 items (Meta, verified 2026-09-26), a story
 * up to `IG_STORY_MAX_FRAMES` frames, a Facebook feed post up to `FB_MULTI_PHOTO_MAX`
 * photos, every other format one.
 */
export const MAX_MEDIA_PER_FORMAT = {
    ig_feed: 1,
    ig_carousel: 10,
    ig_story: IG_STORY_MAX_FRAMES,
    fb_feed: FB_MULTI_PHOTO_MAX,
    ig_reel: 1,
    fb_reel: 1,
    fb_story: IG_STORY_MAX_FRAMES,
};
export function isEnabledVariantFormat(value) {
    return typeof value === 'string' && ENABLED_VARIANT_FORMATS.includes(value);
}
export function isVariantFormat(value) {
    return typeof value === 'string' && CONTENT_VARIANT_FORMATS.includes(value);
}
//# sourceMappingURL=formats.js.map